import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, BarChart2, ChartNoAxesColumnDecreasing, MessageCircle, Calculator, HelpCircle, Users, LogOut, Upload, Download, Settings, ChevronLeft, ChevronRight, FileText, FlaskConical, FileSearch } from 'lucide-react';
import Logo from '../../assets/images/logo2.png';
import { useAuth } from '../../state/auth/context';
import { useSidebar } from '../../contexts/SidebarContext';

export const Sidebar = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { isExpanded, setIsExpanded } = useSidebar();

  const handleLogout = async () => {
    try {
      await logout();
      // Navigation to the login page is handled in the logout function
      // But we could add additional UI feedback here if needed
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback navigation in case the logout function fails
      navigate('/', { replace: true });
    }
  };

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 transition-all duration-300 ease-in-out z-50 ${
        isExpanded ? 'w-64' : 'w-16'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo Section */}
      <div className="p-4 flex items-center justify-center">
        {isExpanded && (
          <img
            src={Logo}
            alt="Xenith Logo"
            className="h-10 w-auto transition-opacity duration-300"
          />
        )}
      </div>

      {/* Navigation Section */}
      <nav className={`mt-6 transition-all duration-300 ${isExpanded ? 'px-6' : 'px-2'}`}>
        <div className="space-y-1">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                isActive ? 'bg-green-50 text-green-600' : isExpanded ? 'hover:bg-green-50 hover:text-green-600' : 'hover:bg-green-50 hover:text-green-600'
              } group relative`
            }

          >
            <Home className="h-5 w-5 flex-shrink-0" />
            {isExpanded && (
              <span className="transition-all duration-300 opacity-100 translate-x-0">
                Home
              </span>
            )}
          </NavLink>

          <NavLink
            to="/analytics"
            className={({ isActive }) =>
              `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
              } group relative`
            }

          >
            <BarChart2 className="h-5 w-5 flex-shrink-0" />
            {isExpanded && (
              <span className="transition-all duration-300 opacity-100 translate-x-0">
                Projekte
              </span>
            )}
          </NavLink>

          <NavLink
            to="/load"
            className={({ isActive }) =>
              `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
              } group relative`
            }

          >
            <Upload className="h-5 w-5 flex-shrink-0" />
            {isExpanded && (
              <span className="transition-all duration-300 opacity-100 translate-x-0">
                Lieferscheine
              </span>
            )}
          </NavLink>

          <NavLink
            to="/export"
            className={({ isActive }) =>
              `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
              } group relative`
            }

          >
            <Download className="h-5 w-5 flex-shrink-0" />
            {isExpanded && (
              <span className="transition-all duration-300 opacity-100 translate-x-0">
                Export
              </span>
            )}
          </NavLink>

          {/* Temporarily commented out - Check menu item */}
          {/*
          <NavLink
            to="/chat"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-3 py-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors ${
                isActive ? 'bg-green-50 text-green-600' : ''
              }`
            }
          >
            <MessageCircle className="h-5 w-5" />
            <span>Check</span>
          </NavLink>
          */}
        </div>

        {/* Tools Section */}
        <div className="mt-10">
          {isExpanded && (
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider transition-opacity duration-300">
              Tools
            </h3>
          )}
          <div className={`${isExpanded ? 'mt-2' : 'mt-0'} space-y-1`}>
            <NavLink
              to="/simulation"
              className={({ isActive }) =>
                `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                  isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
                } group relative`
              }

            >
              <Calculator className="h-5 w-5 flex-shrink-0" />
              {isExpanded && (
                <span className="transition-all duration-300 opacity-100 translate-x-0">
                  CO2-Bilanzierung
                </span>
              )}
            </NavLink>

            <NavLink
              to="/vergleich"
              className={({ isActive }) =>
                `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                  isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
                } group relative`
              }

            >
              <ChartNoAxesColumnDecreasing className="h-5 w-5 flex-shrink-0" />
              {isExpanded && (
                <span className="transition-all duration-300 opacity-100 translate-x-0">
                  CO2-Maßnahmen
                </span>
              )}
            </NavLink>

            {/* Temporarily commented out - Help menu item */}
            {/*
            <NavLink
              to="/help"
              className={({ isActive }) =>
                `flex items-center space-x-3 px-3 py-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors ${
                  isActive ? 'bg-green-50 text-green-600' : ''
                }`
              }
            >
              <HelpCircle className="h-5 w-5" />
              <span>Help</span>
            </NavLink>
            */}

            <NavLink
              to="/erstpruefungen"
              className={({ isActive }) =>
                `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                  isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
                } group relative`
              }

            >
              <FlaskConical className="h-5 w-5 flex-shrink-0" />
              {isExpanded && (
                <span className="transition-all duration-300 opacity-100 translate-x-0">
                  Erstprüfungen
                </span>
              )}
            </NavLink>

            <NavLink
              to="/leistungsverzeichnisse"
              className={({ isActive }) =>
                `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                  isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
                } group relative`
              }

            >
              <FileText className="h-5 w-5 flex-shrink-0" />
              {isExpanded && (
                <span className="transition-all duration-300 opacity-100 translate-x-0">
                  Leistungsverzeichnisse
                </span>
              )}
            </NavLink>

            <NavLink
              to="/dokumente"
              className={({ isActive }) =>
                `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                  isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
                } group relative`
              }

            >
              <FileSearch className="h-5 w-5 flex-shrink-0" />
              {isExpanded && (
                <span className="transition-all duration-300 opacity-100 translate-x-0">
                  Dokumente
                </span>
              )}
            </NavLink>

            <NavLink
              to="/projects-management"
              className={({ isActive }) =>
                `flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 ${
                  isActive ? 'bg-green-50 text-green-600' : 'hover:bg-green-50 hover:text-green-600'
                } group relative`
              }

            >
              <Settings className="h-5 w-5 flex-shrink-0" />
              {isExpanded && (
                <span className="transition-all duration-300 opacity-100 translate-x-0">
                  Projektverwaltung
                </span>
              )}
            </NavLink>
          </div>
        </div>
      </nav>

      {/* Toggle Button */}
      <div className={`absolute bottom-16 w-full transition-all duration-300 ${isExpanded ? 'px-6' : 'px-2'}`}>
        <button
          onClick={toggleSidebar}
          className={`flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 transition-all duration-300 hover:bg-green-50 hover:text-green-600 w-full group relative`}

        >
          {isExpanded ? <ChevronLeft className="h-5 w-5 flex-shrink-0" /> : <ChevronRight className="h-5 w-5 flex-shrink-0" />}
          {isExpanded && (
            <span className="transition-all duration-300 opacity-100 translate-x-0">
              Einklappen
            </span>
          )}
        </button>
      </div>

      {/* Logout Button */}
      <div className={`absolute bottom-4 w-full transition-all duration-300 ${isExpanded ? 'px-6' : 'px-2'}`}>
        <button
          onClick={handleLogout}
          className={`flex items-center ${isExpanded ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-md text-gray-600 hover:bg-red-50 hover:text-red-600 transition-all duration-300 w-full group relative`}

        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {isExpanded && (
            <span className="transition-all duration-300 opacity-100 translate-x-0">
              Log out
            </span>
          )}
        </button>
      </div>
    </div>
  );
};